import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ContentCopy,
  Delete,
  Edit,
  ManageAccounts,
  MoreVert,
  PersonAddAlt1,
  Search,
  ShieldMoon,
  VpnKey,
} from "@mui/icons-material";
import { EmptyState, PageHeader, PageSkeleton } from "../components";
import CustomSnackbar, {
  CustomSnackbarRef,
} from "../components/CustomSnackbar";
import { useMedia } from "../context/MediaContext";
import { useAuthStore } from "../store/authStore";
import {
  createPanelUser,
  deletePanelUser,
  getPanelRoles,
  getPanelUsers,
  PanelRole,
  PanelUser,
  PasswordIssued,
  resetPanelUserPassword,
  resetUserTwoFactor,
  updatePanelUser,
} from "../utils/api";

const ROLE_LABELS: Record<PanelRole, string> = {
  admin: "Администратор",
  merchant: "Мерчант",
  support: "Поддержка",
  ref: "Реферал",
};

/** Роли различаются оттенком, чтобы список читался без вчитывания в текст. */
const ROLE_TONE: Record<PanelRole, "primary" | "secondary" | "default"> = {
  admin: "primary",
  merchant: "secondary",
  support: "default",
  ref: "default",
};

const roleLabel = (role: string) =>
  ROLE_LABELS[role as PanelRole] ?? role;

/**
 * Достаёт человекочитаемый текст из detail. Бэкенд возвращает его и строкой,
 * и объектом — у ролей это {message, allowed}, у мерчанта {message, merchant_id}.
 */
const errorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (detail.message) {
    return detail.merchant_id
      ? `${detail.message} (мерчант #${detail.merchant_id})`
      : detail.message;
  }
  return fallback;
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/** Умещается в один экран на ноутбуке и не требует прокрутки таблицы. */
const PAGE_SIZE = 12;

type FormState = { username: string; role: PanelRole; password: string };

const EMPTY_FORM: FormState = { username: "", role: "support", password: "" };

const UsersPage = () => {
  const theme = useTheme();
  const { isMobile600 } = useMedia();
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const currentUser = useAuthStore((state) => state.user) as any;
  const currentUsername: string | null =
    typeof currentUser === "string"
      ? currentUser
      : currentUser?.username ?? null;

  const [users, setUsers] = useState<PanelUser[]>([]);
  const [roles, setRoles] = useState<PanelRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [roleFilter, setRoleFilter] = useState<PanelRole | "">("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Бэкенд отдаёт всех пользователей одним списком, поэтому страницы режем здесь.
  const [page, setPage] = useState(1);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<PanelUser | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PanelUser | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [issued, setIssued] = useState<PasswordIssued | null>(null);
  const [confirm, setConfirm] = useState<{
    kind: "delete" | "twofa";
    user: PanelUser;
  } | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Поиск идёт на сервер, поэтому ввод придерживается, а не шлётся посимвольно.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPanelUsers({
        role: roleFilter,
        search: debouncedSearch,
      });
      setUsers(response.data ?? []);
      setPage(1);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось загрузить пользователей"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [roleFilter, debouncedSearch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    getPanelRoles()
      .then((response) => setRoles(response.data?.roles ?? []))
      // Без списка ролей форма всё равно работает на встроенном наборе.
      .catch(() => setRoles(Object.keys(ROLE_LABELS) as PanelRole[]));
  }, []);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (user: PanelUser) => {
    setEditing(user);
    setForm({ username: user.username, role: user.role, password: "" });
    setFormOpen(true);
    closeMenu();
  };

  const submitForm = async () => {
    const username = form.username.trim();
    if (username.length < 3) {
      snackbarRef.current?.showSnackbar(
        "Имя не короче трёх символов",
        "warning",
      );
      return;
    }
    if (!editing && form.password && form.password.length < 8) {
      snackbarRef.current?.showSnackbar(
        "Пароль не короче восьми символов",
        "warning",
      );
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        // Отправляем только изменённое: PATCH принимает оба поля необязательными.
        const payload: { username?: string; role?: PanelRole } = {};
        if (username !== editing.username) payload.username = username;
        if (form.role !== editing.role) payload.role = form.role;

        if (!Object.keys(payload).length) {
          setFormOpen(false);
          return;
        }
        await updatePanelUser(editing.id, payload);
        snackbarRef.current?.showSnackbar("Пользователь обновлён", "success");
      } else {
        const response = await createPanelUser({
          username,
          role: form.role,
          password: form.password || undefined,
        });
        snackbarRef.current?.showSnackbar("Пользователь создан", "success");
        if (response.data?.password) setIssued(response.data);
      }
      setFormOpen(false);
      loadUsers();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось сохранить"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (user: PanelUser) => {
    closeMenu();
    try {
      const response = await resetPanelUserPassword(user.id);
      setIssued(response.data);
      snackbarRef.current?.showSnackbar(
        "Пароль сброшен, сессии прерваны",
        "success",
      );
      loadUsers();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось сбросить пароль"),
        "error",
      );
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setConfirming(true);
    try {
      if (confirm.kind === "delete") {
        await deletePanelUser(confirm.user.id);
        snackbarRef.current?.showSnackbar("Пользователь удалён", "success");
      } else {
        await resetUserTwoFactor(confirm.user.id);
        snackbarRef.current?.showSnackbar("Второй фактор снят", "success");
      }
      setConfirm(null);
      loadUsers();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(
          err,
          confirm.kind === "delete"
            ? "Не удалось удалить"
            : "Не удалось сбросить 2FA",
        ),
        "error",
      );
    } finally {
      setConfirming(false);
    }
  };

  const copyPassword = () => {
    if (!issued?.password) return;
    navigator.clipboard
      .writeText(issued.password)
      .then(() =>
        snackbarRef.current?.showSnackbar("Пароль скопирован", "success"),
      )
      .catch(() =>
        snackbarRef.current?.showSnackbar("Не удалось скопировать", "error"),
      );
  };

  const availableRoles = useMemo(
    () => (roles.length ? roles : (Object.keys(ROLE_LABELS) as PanelRole[])),
    [roles],
  );

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));

  // Смена фильтров и удаление пользователя могут оставить страницу за пределами
  // списка — тогда таблица оказалась бы пустой без видимой причины.
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pageUsers = useMemo(
    () => users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [users, page],
  );

  const isSelf = (user: PanelUser) =>
    currentUsername !== null && user.username === currentUsername;

  const twoFactorCell = (user: PanelUser) => {
    if (user.twofa_locked) {
      return (
        <Chip
          size="small"
          label="Заблокирован"
          sx={{
            fontWeight: 600,
            color: theme.palette.semanticText.error,
            bgcolor: "transparent",
            border: `1px solid ${theme.palette.semanticText.error}`,
          }}
        />
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: user.twofa_enabled
            ? theme.palette.semanticText.success
            : theme.palette.text.secondary,
        }}
      >
        {user.twofa_enabled ? "Включён" : "Выключен"}
      </Typography>
    );
  };

  const actionsButton = (user: PanelUser) => (
    <IconButton
      size="small"
      aria-label={`Действия с ${user.username}`}
      onClick={(event) => {
        setMenuAnchor(event.currentTarget);
        setMenuUser(user);
      }}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  );

  const renderContent = () => {
    if (loading) return <PageSkeleton />;

    if (failed) {
      return (
        <EmptyState
          icon={ManageAccounts}
          title="Список недоступен"
          description="Не удалось получить пользователей. Раздел доступен только администратору."
          action={
            <Button variant="contained" onClick={loadUsers}>
              Повторить
            </Button>
          }
        />
      );
    }

    if (!users.length) {
      return (
        <EmptyState
          icon={ManageAccounts}
          title="Пользователей нет"
          description={
            roleFilter || debouncedSearch
              ? "По выбранным условиям ничего не найдено."
              : "Создайте первого пользователя панели."
          }
          action={
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1 />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          }
        />
      );
    }

    if (isMobile600) {
      return (
        <Stack spacing={1.5}>
          {pageUsers.map((user) => (
            <Paper
              key={user.id}
              variant="outlined"
              sx={{ p: 2, borderRadius: "16px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ fontWeight: 700, wordBreak: "break-all" }}
                  >
                    {user.username}
                    {isSelf(user) && (
                      <Chip
                        size="small"
                        label="вы"
                        sx={{ ml: 1, height: 18, fontSize: 11 }}
                      />
                    )}
                  </Typography>
                  <Chip
                    size="small"
                    color={ROLE_TONE[user.role]}
                    label={roleLabel(user.role)}
                    sx={{ mt: 0.75, fontWeight: 600 }}
                  />
                </Box>
                {actionsButton(user)}
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Второй фактор
                  </Typography>
                  {twoFactorCell(user)}
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Создан
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(user.created_at)}
                  </Typography>
                </Stack>
                {user.merchant_id !== null && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Мерчант
                    </Typography>
                    <Typography variant="body2">#{user.merchant_id}</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      );
    }

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "16px" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.table.head }}>
              <TableCell sx={{ fontWeight: 700 }}>Пользователь</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Роль</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Второй фактор</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Создан</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Пароль изменён</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {user.username}
                    </Typography>
                    {isSelf(user) && (
                      <Chip
                        size="small"
                        label="вы"
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                    {user.merchant_id !== null && (
                      <Tooltip title="На пользователе висит мерчант — роль менять нельзя">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`мерчант #${user.merchant_id}`}
                          sx={{ height: 18, fontSize: 11 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={ROLE_TONE[user.role]}
                    label={roleLabel(user.role)}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>{twoFactorCell(user)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.password_changed_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{actionsButton(user)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    // Layout отдаёт страницам контейнер с overflow: hidden, прокрутку каждая
    // страница делает у себя — иначе длинный список просто обрезается.
    <Box
      sx={{
        width: "100%",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
        // Тот же боковой отступ, что у остальных разделов панели.
        p: { xs: 2, sm: 3 },
      }}
    >
      <PageHeader
        icon={ManageAccounts}
        title="Пользователи"
        description="Учётные записи панели: роли, пароли и второй фактор"
        actions={
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1 />}
            onClick={openCreate}
          >
            Добавить
          </Button>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Поиск по имени"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small"
          displayEmpty
          value={roleFilter}
          onChange={(event) =>
            setRoleFilter(event.target.value as PanelRole | "")
          }
          sx={{ minWidth: { xs: "100%", sm: 200 } }}
        >
          <MenuItem value="">Все роли</MenuItem>
          {availableRoles.map((role) => (
            <MenuItem key={role} value={role}>
              {roleLabel(role)}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {renderContent()}

      {!loading && !failed && users.length > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {`Показаны ${(page - 1) * PAGE_SIZE + 1}–${Math.min(
              page * PAGE_SIZE,
              users.length,
            )} из ${users.length}`}
          </Typography>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event, value) => setPage(value)}
              color="primary"
              shape="rounded"
              size={isMobile600 ? "small" : "medium"}
            />
          )}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => menuUser && openEdit(menuUser)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Изменить</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuUser && resetPassword(menuUser)}>
          <ListItemIcon>
            <VpnKey fontSize="small" />
          </ListItemIcon>
          <ListItemText>Сбросить пароль</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!menuUser?.twofa_enabled && !menuUser?.twofa_locked}
          onClick={() => {
            if (menuUser) setConfirm({ kind: "twofa", user: menuUser });
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ShieldMoon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Сбросить 2FA</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={Boolean(menuUser && isSelf(menuUser))}
          onClick={() => {
            if (menuUser) setConfirm({ kind: "delete", user: menuUser });
            closeMenu();
          }}
          sx={{ color: theme.palette.semanticText.error }}
        >
          <ListItemIcon>
            <Delete
              fontSize="small"
              sx={{ color: theme.palette.semanticText.error }}
            />
          </ListItemIcon>
          <ListItemText>Удалить</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? "Изменение пользователя" : "Новый пользователь"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Имя пользователя"
              size="small"
              fullWidth
              value={form.username}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, username: event.target.value }))
              }
            />
            <Select
              size="small"
              fullWidth
              value={form.role}
              disabled={Boolean(editing && editing.merchant_id !== null)}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  role: event.target.value as PanelRole,
                }))
              }
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {roleLabel(role)}
                </MenuItem>
              ))}
            </Select>

            {editing && editing.merchant_id !== null && (
              <Typography variant="caption" color="text.secondary">
                На пользователе висит мерчант #{editing.merchant_id}, роль
                должна остаться «Мерчант».
              </Typography>
            )}

            {!editing && (
              <>
                <TextField
                  label="Пароль"
                  size="small"
                  fullWidth
                  value={form.password}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      password: event.target.value,
                    }))
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  Оставьте пустым — сгенерируем стойкий пароль и покажем один
                  раз.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={submitForm} disabled={saving}>
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : editing ? (
              "Сохранить"
            ) : (
              "Создать"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(issued?.password)}
        onClose={() => setIssued(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Пароль выдан</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Пароль для «{issued?.username}». Он показывается один раз —
            скопируйте сейчас, восстановить его будет негде.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: 15,
                wordBreak: "break-all",
              }}
            >
              {issued?.password}
            </Typography>
            <IconButton size="small" onClick={copyPassword} aria-label="Копировать">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Paper>
          {issued?.sessions_revoked && (
            <Typography variant="caption" color="text.secondary">
              Все прежние сессии пользователя прерваны.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setIssued(null)}>
            Готово
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirm)}
        onClose={() => !confirming && setConfirm(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirm?.kind === "delete"
            ? "Удалить пользователя?"
            : "Снять второй фактор?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirm?.kind === "delete"
              ? `Учётная запись «${confirm.user.username}» будет удалена без возможности вернуть.`
              : `Пользователь «${confirm?.user.username}» сможет войти без кода и настроит второй фактор заново.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={confirming}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color={confirm?.kind === "delete" ? "error" : "primary"}
            onClick={runConfirmed}
            disabled={confirming}
          >
            {confirming ? (
              <CircularProgress size={20} color="inherit" />
            ) : confirm?.kind === "delete" ? (
              "Удалить"
            ) : (
              "Сбросить"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar ref={snackbarRef} />
    </Box>
  );
};

export default UsersPage;
